/**
 * <s-sticky-column> — keeps a sticky column fully reachable.
 *
 * The element is `position: sticky` via utilities. When it is shorter than the viewport it
 * sticks at the gap in data-offset (px, default 24). When it is taller, its sticky `top`
 * becomes negative so its bottom edge sticks to the viewport bottom instead, so nothing in
 * the column is ever out of reach. Writes only the --sticky-top custom property on itself.
 */
class StickyColumn extends HTMLElement {
  #resizeObserver = null;
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this);
    window.addEventListener('resize', () => this.#update(), { signal: this.#abort.signal, passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#abort?.abort();
  }

  #update() {
    const offset = Number(this.dataset.offset || 24);
    const top = Math.min(offset, window.innerHeight - this.offsetHeight - offset);
    this.style.setProperty('--sticky-top', `${top}px`);
  }
}

if (!customElements.get('s-sticky-column')) customElements.define('s-sticky-column', StickyColumn);
